/**
 * Component type enumeration representing hierarchy levels
 */
export var ComponentType;
(function (ComponentType) {
    ComponentType["SYSTEM"] = "SYSTEM";
    ComponentType["SUBSYSTEM"] = "SUBSYSTEM";
    ComponentType["COMPONENT"] = "COMPONENT";
    ComponentType["PART"] = "PART";
})(ComponentType || (ComponentType = {}));
/**
 * INTERLOCK systemClass wave — scopes the delivered-robot analysis surfaces
 * (System diagram + FMEA/Functions) to SYSTEM components; SUPPORT (test/
 * verification tooling, e.g. a simulation rig) and EXTERNAL (client-side
 * counterparty systems, e.g. QCells) stay full Interface Matrix participants
 * but never appear as system-diagram blocks or FMEA-analysable components.
 */
export var SystemClass;
(function (SystemClass) {
    SystemClass["SYSTEM"] = "SYSTEM";
    SystemClass["SUPPORT"] = "SUPPORT";
    SystemClass["EXTERNAL"] = "EXTERNAL";
})(SystemClass || (SystemClass = {}));
/**
 * Verification status for commissioning tracking
 */
export var VerificationStatus;
(function (VerificationStatus) {
    VerificationStatus["NOT_STARTED"] = "NOT_STARTED";
    VerificationStatus["IN_PROGRESS"] = "IN_PROGRESS";
    VerificationStatus["VERIFIED"] = "VERIFIED";
    VerificationStatus["FAILED"] = "FAILED";
})(VerificationStatus || (VerificationStatus = {}));
/**
 * Type of cause-effect relationship
 */
export var CauseEffectType;
(function (CauseEffectType) {
    CauseEffectType["FAILURE_MODE"] = "FAILURE_MODE";
    CauseEffectType["ROOT_CAUSE"] = "ROOT_CAUSE";
})(CauseEffectType || (CauseEffectType = {}));
/**
 * FEA-041: Analysis depth tracking for hierarchical FMEA
 */
export var AnalysisDepth;
(function (AnalysisDepth) {
    AnalysisDepth["SURFACE"] = "SURFACE";
    AnalysisDepth["DETAILED"] = "DETAILED";
})(AnalysisDepth || (AnalysisDepth = {}));
/**
 * Type guard to check if a string is a valid AnalysisDepth
 */
export function isAnalysisDepth(value) {
    return Object.values(AnalysisDepth).includes(value);
}
/**
 * Display labels for analysis depth
 */
export const ANALYSIS_DEPTH_LABELS = {
    [AnalysisDepth.SURFACE]: 'Surface Level',
    [AnalysisDepth.DETAILED]: 'Detailed Analysis',
};
/**
 * 5M (6M) categories for fishbone/Ishikawa diagrams
 */
export var Category5M;
(function (Category5M) {
    Category5M["MAN"] = "MAN";
    Category5M["MACHINE"] = "MACHINE";
    Category5M["METHOD"] = "METHOD";
    Category5M["MATERIAL"] = "MATERIAL";
    Category5M["MEASUREMENT"] = "MEASUREMENT";
    Category5M["ENVIRONMENT"] = "ENVIRONMENT";
})(Category5M || (Category5M = {}));
// Intentional type+value merge (TS-valid) — the repo's base `no-redeclare`
// (JS-only, no @typescript-eslint override) can't tell a type alias from a
// value declaration apart. Pre-existing pattern, newly surfaced by the
// pre-commit hook once this file needed touching for SystemClass.
// eslint-disable-next-line no-redeclare
export const FMEACategory = Category5M;
/**
 * Risk level enumeration
 */
export var RiskLevel;
(function (RiskLevel) {
    RiskLevel["CRITICAL"] = "CRITICAL";
    RiskLevel["HIGH"] = "HIGH";
    RiskLevel["MEDIUM"] = "MEDIUM";
    RiskLevel["LOW"] = "LOW";
})(RiskLevel || (RiskLevel = {}));
/**
 * Risk level thresholds based on RPN (Risk Priority Number)
 * RPN = Severity × Occurrence × Detection (max: 10 × 10 × 10 = 1000)
 *
 * Thresholds match the canonical state-based FMEA specification:
 * - LOW: RPN ≤ 40
 * - MEDIUM: RPN 41-120
 * - HIGH: RPN 121-200
 * - CRITICAL: RPN > 200
 */
export const RPN_THRESHOLDS = {
    LOW: 0, // 0-40
    MEDIUM: 40, // 41-120
    HIGH: 120, // 121-200
    CRITICAL: 200, // 201+
};
/**
 * Calculate risk level from RPN value
 * @param rpn - Risk Priority Number (Severity × Occurrence × Detection)
 * @returns Risk level classification
 *
 * @example
 * ```typescript
 * getRiskLevel(35) // Returns RiskLevel.LOW
 * getRiskLevel(100) // Returns RiskLevel.MEDIUM
 * getRiskLevel(150) // Returns RiskLevel.HIGH
 * getRiskLevel(300) // Returns RiskLevel.CRITICAL
 * ```
 */
export function getRiskLevel(rpn) {
    if (rpn > RPN_THRESHOLDS.CRITICAL)
        return RiskLevel.CRITICAL;
    if (rpn > RPN_THRESHOLDS.HIGH)
        return RiskLevel.HIGH;
    if (rpn > RPN_THRESHOLDS.MEDIUM)
        return RiskLevel.MEDIUM;
    return RiskLevel.LOW;
}
/**
 * Legacy alias for getRiskLevel (deprecated)
 * @deprecated Use getRiskLevel instead
 */
export const calculateRiskLevel = getRiskLevel;
export const ScoringModelValues = ['RPN', 'LXS'];
/**
 * L×S (Likelihood × Severity) band thresholds for the 5×5 HAZID model.
 * Bands: 1–4 Low · 5–9 Medium · 10–14 High · 15–25 Critical.
 * (Thresholds are the lower-exclusive bound of each band, mirroring RPN_THRESHOLDS.)
 */
export const LXS_THRESHOLDS = {
    LOW: 0, // 1-4
    MEDIUM: 4, // 5-9
    HIGH: 9, // 10-14
    CRITICAL: 14, // 15-25
};
/**
 * Map an L×S score (1–25) to a RiskLevel using the 5×5 HAZID bands.
 */
export function getLxsRiskLevel(score) {
    if (score > LXS_THRESHOLDS.CRITICAL)
        return RiskLevel.CRITICAL;
    if (score > LXS_THRESHOLDS.HIGH)
        return RiskLevel.HIGH;
    if (score > LXS_THRESHOLDS.MEDIUM)
        return RiskLevel.MEDIUM;
    return RiskLevel.LOW;
}
/**
 * Model-aware risk level. The single source of truth for banding a score once
 * the project's scoring model is known.
 * - RPN → S×O×D thresholds (>200 Critical, 121-200 High, 41-120 Med, ≤40 Low).
 * - LXS → L×S 5×5 bands (15-25 Critical, 10-14 High, 5-9 Med, 1-4 Low).
 *
 * @example
 * getRiskLevelForModel(200, 'RPN') // HIGH  (S8×O5×D5)
 * getRiskLevelForModel(20, 'LXS')  // CRITICAL (L4×S5)
 */
export function getRiskLevelForModel(score, model = 'RPN') {
    return model === 'LXS' ? getLxsRiskLevel(score) : getRiskLevel(score);
}
/**
 * Model-aware score from raw axes.
 * - RPN → severity × occurrence × detection.
 * - LXS → severity × occurrence (occurrence carries Likelihood; detection ignored).
 */
export function computeScoreForModel(severity, occurrence, detection, model = 'RPN') {
    return model === 'LXS' ? severity * occurrence : severity * occurrence * detection;
}
/**
 * Axis maximum per model (10 for RPN, 5 for LXS). Drives matrix grid size and
 * input clamps in the UI.
 */
export function scoringAxisMax(model) {
    return model === 'LXS' ? 5 : 10;
}
/**
 * Human-readable band ranges per scoring model, keyed by risk level. Use in
 * legends, threshold hints and KPI subtitles so an LXS project reads its 1-25
 * L×S bands instead of the RPN 40/120/200 ranges. Single source of truth for
 * band copy — do NOT re-hardcode "> 200" / "15-25" in components.
 *
 * @example
 * getScoringBandLabels('LXS').critical // '15-25'
 * getScoringBandLabels('RPN').critical // '> 200'
 */
export function getScoringBandLabels(model) {
    return model === 'LXS'
        ? { critical: '15-25', high: '10-14', medium: '5-9', low: '1-4' }
        : { critical: '> 200', high: '121-200', medium: '41-120', low: '≤ 40' };
}
/**
 * Color mapping for risk levels (Material Design palette)
 */
export const RISK_COLORS = {
    LOW: '#4CAF50', // Green
    MEDIUM: '#FFC107', // Yellow/Amber
    HIGH: '#FF9800', // Orange
    CRITICAL: '#F44336', // Red
};
/**
 * Type guard to check if a string is a valid ComponentType
 */
export function isComponentType(value) {
    return Object.values(ComponentType).includes(value);
}
/**
 * Type guard to check if a string is a valid VerificationStatus
 */
export function isVerificationStatus(value) {
    return Object.values(VerificationStatus).includes(value);
}
/**
 * Type guard to check if a string is a valid CauseEffectType
 */
export function isCauseEffectType(value) {
    return Object.values(CauseEffectType).includes(value);
}
/**
 * Type guard to check if a string is a valid Category5M
 */
export function isCategory5M(value) {
    return Object.values(Category5M).includes(value);
}
/**
 * Commissioning Activity Status for tracking workflow progress
 * Matches Prisma schema ActivityStatus enum
 */
export var ActivityStatus;
(function (ActivityStatus) {
    ActivityStatus["NOT_STARTED"] = "NOT_STARTED";
    ActivityStatus["PLANNED"] = "PLANNED";
    ActivityStatus["IN_PROGRESS"] = "IN_PROGRESS";
    ActivityStatus["COMPLETED"] = "COMPLETED";
    ActivityStatus["BLOCKED"] = "BLOCKED";
})(ActivityStatus || (ActivityStatus = {}));
/**
 * Type guard to check if a string is a valid ActivityStatus
 */
export function isActivityStatus(value) {
    return Object.values(ActivityStatus).includes(value);
}
/**
 * FEA-015: Commissioning phases for granular testing organization
 * Matches Prisma schema CommissioningPhase enum
 */
export var CommissioningPhase;
(function (CommissioningPhase) {
    CommissioningPhase["ENERGISATION"] = "ENERGISATION";
    CommissioningPhase["COLD_COMMISSIONING"] = "COLD_COMMISSIONING";
    CommissioningPhase["HOT_COMMISSIONING"] = "HOT_COMMISSIONING";
})(CommissioningPhase || (CommissioningPhase = {}));
/**
 * Type guard to check if a string is a valid CommissioningPhase
 */
export function isCommissioningPhase(value) {
    return Object.values(CommissioningPhase).includes(value);
}
/**
 * Display labels for commissioning phases
 */
export const COMMISSIONING_PHASE_LABELS = {
    [CommissioningPhase.ENERGISATION]: 'Energisation',
    [CommissioningPhase.COLD_COMMISSIONING]: 'Cold Commissioning',
    [CommissioningPhase.HOT_COMMISSIONING]: 'Hot Commissioning',
};
/**
 * FEA-051: Function criticality levels for functional architecture
 * Matches Prisma schema FunctionCriticality enum
 */
export var FunctionCriticality;
(function (FunctionCriticality) {
    FunctionCriticality["SAFETY"] = "SAFETY";
    FunctionCriticality["MISSION"] = "MISSION";
    FunctionCriticality["OPERATIONAL"] = "OPERATIONAL";
})(FunctionCriticality || (FunctionCriticality = {}));
/**
 * Type guard to check if a string is a valid FunctionCriticality
 */
export function isFunctionCriticality(value) {
    return Object.values(FunctionCriticality).includes(value);
}
/**
 * Display labels for function criticality levels
 */
export const FUNCTION_CRITICALITY_LABELS = {
    [FunctionCriticality.SAFETY]: 'Safety Critical',
    [FunctionCriticality.MISSION]: 'Mission Critical',
    [FunctionCriticality.OPERATIONAL]: 'Operational',
};
/**
 * Colors for criticality badges (matches RISK_COLORS pattern)
 */
export const FUNCTION_CRITICALITY_COLORS = {
    [FunctionCriticality.SAFETY]: '#F44336', // Red (critical)
    [FunctionCriticality.MISSION]: '#FF9800', // Orange (high)
    [FunctionCriticality.OPERATIONAL]: '#2196F3', // Blue (informational)
};
/**
 * FEA-016: Injection methods for fault injection testing
 * Matches Prisma schema InjectionMethod enum
 */
export var InjectionMethod;
(function (InjectionMethod) {
    InjectionMethod["NONE"] = "NONE";
    InjectionMethod["PHYSICAL"] = "PHYSICAL";
    InjectionMethod["ELECTRICAL"] = "ELECTRICAL";
    InjectionMethod["SOFTWARE"] = "SOFTWARE";
    InjectionMethod["PROCEDURAL"] = "PROCEDURAL";
    InjectionMethod["ENVIRONMENTAL"] = "ENVIRONMENTAL";
})(InjectionMethod || (InjectionMethod = {}));
/**
 * Type guard to check if a string is a valid InjectionMethod
 */
export function isInjectionMethod(value) {
    return Object.values(InjectionMethod).includes(value);
}
/**
 * Display labels for injection methods
 */
export const INJECTION_METHOD_LABELS = {
    [InjectionMethod.NONE]: 'None (Normal Operation)',
    [InjectionMethod.PHYSICAL]: 'Physical Intervention',
    [InjectionMethod.ELECTRICAL]: 'Electrical Injection',
    [InjectionMethod.SOFTWARE]: 'Software Force',
    [InjectionMethod.PROCEDURAL]: 'Procedural',
    [InjectionMethod.ENVIRONMENTAL]: 'Environmental',
};
/**
 * FEA-053 + FEA-066 + FEA-146: Session types for guided FMEA workflows
 * Simplified to 3 main session types matching project lifecycle
 */
export var SessionType;
(function (SessionType) {
    // Primary session types (3-phase project lifecycle)
    SessionType["KICKOFF"] = "KICKOFF";
    SessionType["DEEP_DIVE"] = "DEEP_DIVE";
    SessionType["CLOSEOUT"] = "CLOSEOUT";
})(SessionType || (SessionType = {}));
/**
 * Type guard to check if a string is a valid SessionType
 */
export function isSessionType(value) {
    return Object.values(SessionType).includes(value);
}
/**
 * Project phase for each session type
 */
export const SESSION_TYPE_PHASE = {
    [SessionType.KICKOFF]: 'Early',
    [SessionType.DEEP_DIVE]: 'Engineering',
    [SessionType.CLOSEOUT]: 'Final',
};
/**
 * Display labels for session types
 */
export const SESSION_TYPE_LABELS = {
    [SessionType.KICKOFF]: 'Kickoff',
    [SessionType.DEEP_DIVE]: 'Deep Dive',
    [SessionType.CLOSEOUT]: 'Closeout',
};
/**
 * Session type descriptions for UI tooltips
 */
export const SESSION_TYPE_DESCRIPTIONS = {
    [SessionType.KICKOFF]: 'Define mission, build block diagram, surface-level risk identification',
    [SessionType.DEEP_DIVE]: 'Detailed FM/RC/Mitigation workshops with full scoring',
    [SessionType.CLOSEOUT]: 'Log outcomes, lessons learned, generate final report',
};
/**
 * Session type icons (emoji)
 */
export const SESSION_TYPE_ICONS = {
    [SessionType.KICKOFF]: '🚀',
    [SessionType.DEEP_DIVE]: '🔬',
    [SessionType.CLOSEOUT]: '✅',
};
/**
 * FEA-053: Session status for tracking progress
 * Matches Prisma schema SessionStatus enum
 */
export var SessionStatus;
(function (SessionStatus) {
    SessionStatus["ACTIVE"] = "ACTIVE";
    SessionStatus["PAUSED"] = "PAUSED";
    SessionStatus["COMPLETED"] = "COMPLETED";
    SessionStatus["ABANDONED"] = "ABANDONED";
})(SessionStatus || (SessionStatus = {}));
/**
 * Type guard to check if a string is a valid SessionStatus
 */
export function isSessionStatus(value) {
    return Object.values(SessionStatus).includes(value);
}
/**
 * Display labels for session status
 */
export const SESSION_STATUS_LABELS = {
    [SessionStatus.ACTIVE]: 'Active',
    [SessionStatus.PAUSED]: 'Paused',
    [SessionStatus.COMPLETED]: 'Completed',
    [SessionStatus.ABANDONED]: 'Abandoned',
};
/**
 * FEA-055: Stress categories for stress library
 * Matches Prisma schema StressCategory enum
 */
export var StressCategory;
(function (StressCategory) {
    StressCategory["ENVIRONMENTAL"] = "ENVIRONMENTAL";
    StressCategory["OPERATIONAL"] = "OPERATIONAL";
    StressCategory["TEMPORAL"] = "TEMPORAL";
    StressCategory["EXTERNAL"] = "EXTERNAL";
})(StressCategory || (StressCategory = {}));
/**
 * Type guard to check if a string is a valid StressCategory
 */
export function isStressCategory(value) {
    return Object.values(StressCategory).includes(value);
}
/**
 * Display labels for stress categories
 */
export const STRESS_CATEGORY_LABELS = {
    [StressCategory.ENVIRONMENTAL]: 'Environmental',
    [StressCategory.OPERATIONAL]: 'Operational',
    [StressCategory.TEMPORAL]: 'Temporal',
    [StressCategory.EXTERNAL]: 'External',
};
/**
 * Stress category descriptions for UI tooltips
 */
export const STRESS_CATEGORY_DESCRIPTIONS = {
    [StressCategory.ENVIRONMENTAL]: 'Temperature, humidity, dust, vibration factors',
    [StressCategory.OPERATIONAL]: 'Overload, misuse, edge cases, and operating conditions',
    [StressCategory.TEMPORAL]: 'Aging, wear, fatigue, and time-based degradation',
    [StressCategory.EXTERNAL]: 'EMI, power quality, network, and external interference',
};
/**
 * Icon/emoji suggestions for stress categories
 */
export const STRESS_CATEGORY_ICONS = {
    [StressCategory.ENVIRONMENTAL]: '🌡️',
    [StressCategory.OPERATIONAL]: '⚙️',
    [StressCategory.TEMPORAL]: '⏳',
    [StressCategory.EXTERNAL]: '⚡',
};
/**
 * FEA-144: Report-specific analysis depth tracking for Sub-System Risk Reports
 */
export var ReportAnalysisDepth;
(function (ReportAnalysisDepth) {
    ReportAnalysisDepth["HIGH_LEVEL"] = "HIGH_LEVEL";
    ReportAnalysisDepth["COMPONENT_LEVEL"] = "COMPONENT_LEVEL";
    ReportAnalysisDepth["PART_LEVEL"] = "PART_LEVEL";
})(ReportAnalysisDepth || (ReportAnalysisDepth = {}));
/**
 * Type guard for ReportAnalysisDepth
 */
export function isReportAnalysisDepth(value) {
    return Object.values(ReportAnalysisDepth).includes(value);
}
/**
 * Display labels for report analysis depth
 */
export const REPORT_ANALYSIS_DEPTH_LABELS = {
    [ReportAnalysisDepth.HIGH_LEVEL]: 'System Level',
    [ReportAnalysisDepth.COMPONENT_LEVEL]: 'Component-Level Analysis',
    [ReportAnalysisDepth.PART_LEVEL]: 'Part-Level Decomposition',
};
/**
 * Progress percentages for analysis depth visualization
 */
export const REPORT_ANALYSIS_DEPTH_PROGRESS = {
    [ReportAnalysisDepth.HIGH_LEVEL]: 33,
    [ReportAnalysisDepth.COMPONENT_LEVEL]: 66,
    [ReportAnalysisDepth.PART_LEVEL]: 100,
};
/**
 * Requirement type classification for requirements traceability (Gap 5).
 * Matches Prisma schema RequirementType enum exactly — this is the single
 * source of truth for both frontend dropdown options and backend
 * create/filter validation. Do NOT hand-roll a divergent option list next
 * to this one (a stale "NON_FUNCTIONAL"/frontend-only value here 400s the
 * whole requirements list when sent as a filter query param).
 */
export var RequirementType;
(function (RequirementType) {
    RequirementType["FUNCTIONAL"] = "FUNCTIONAL";
    RequirementType["SAFETY"] = "SAFETY";
    RequirementType["REGULATORY"] = "REGULATORY";
    RequirementType["PERFORMANCE"] = "PERFORMANCE";
    RequirementType["RELIABILITY"] = "RELIABILITY";
    RequirementType["QUALITY"] = "QUALITY";
    RequirementType["ENVIRONMENTAL"] = "ENVIRONMENTAL";
    // Appended (fidelity fields migration) — append-only in Prisma schema too,
    // keep in sync with prisma/schema.prisma `enum RequirementType`.
    RequirementType["INTERFACE"] = "INTERFACE";
    RequirementType["DESIGN_CONSTRAINT"] = "DESIGN_CONSTRAINT";
    RequirementType["ACCEPTANCE"] = "ACCEPTANCE";
})(RequirementType || (RequirementType = {}));
/**
 * Type guard to check if a string is a valid RequirementType
 */
export function isRequirementType(value) {
    return Object.values(RequirementType).includes(value);
}
/**
 * Display labels for requirement types
 */
export const REQUIREMENT_TYPE_LABELS = {
    [RequirementType.FUNCTIONAL]: 'Functional',
    [RequirementType.SAFETY]: 'Safety',
    [RequirementType.REGULATORY]: 'Regulatory',
    [RequirementType.PERFORMANCE]: 'Performance',
    [RequirementType.RELIABILITY]: 'Reliability',
    [RequirementType.QUALITY]: 'Quality',
    [RequirementType.ENVIRONMENTAL]: 'Environmental',
    [RequirementType.INTERFACE]: 'Interface',
    [RequirementType.DESIGN_CONSTRAINT]: 'Design Constraint',
    [RequirementType.ACCEPTANCE]: 'Acceptance',
};
// =============================================================================
// FEA-211: Unified Navigation & Sessions System
// =============================================================================
/**
 * FEA-211: Session mode - distinguishes work sessions from meeting sessions
 */
export var SessionMode;
(function (SessionMode) {
    SessionMode["WORK"] = "WORK";
    SessionMode["MEETING"] = "MEETING";
})(SessionMode || (SessionMode = {}));
/**
 * Type guard for SessionMode
 */
export function isSessionMode(value) {
    return Object.values(SessionMode).includes(value);
}
/**
 * FEA-211: Meeting types aligned with 6-phase FMEA workflow
 */
export var MeetingType;
(function (MeetingType) {
    // Phase 1: DEFINE
    MeetingType["DEFINE_MISSION"] = "DEFINE_MISSION";
    MeetingType["DEFINE_REQUIREMENTS"] = "DEFINE_REQUIREMENTS";
    // Phase 2: MODEL
    MeetingType["MODEL_STRUCTURE"] = "MODEL_STRUCTURE";
    // Phase 3: ANALYZE
    MeetingType["ANALYZE_FM"] = "ANALYZE_FM";
    MeetingType["ANALYZE_RC"] = "ANALYZE_RC";
    // Phase 4: MITIGATE
    MeetingType["MITIGATE_WORKSHOP"] = "MITIGATE_WORKSHOP";
    MeetingType["MITIGATE_APPROVAL"] = "MITIGATE_APPROVAL";
    // Phase 5: IMPLEMENT
    MeetingType["IMPLEMENT_REVIEW"] = "IMPLEMENT_REVIEW";
    // Phase 6: VERIFY
    MeetingType["VERIFY_COMMISSION"] = "VERIFY_COMMISSION";
    MeetingType["VERIFY_CLOSEOUT"] = "VERIFY_CLOSEOUT";
    // Other
    MeetingType["GENERAL"] = "GENERAL";
    MeetingType["AD_HOC"] = "AD_HOC";
})(MeetingType || (MeetingType = {}));
/**
 * Type guard for MeetingType
 */
export function isMeetingType(value) {
    return Object.values(MeetingType).includes(value);
}
/**
 * Display labels for meeting types
 */
export const MEETING_TYPE_LABELS = {
    [MeetingType.DEFINE_MISSION]: '① DEFINE: Mission & Scope',
    [MeetingType.DEFINE_REQUIREMENTS]: '① DEFINE: Requirements Review',
    [MeetingType.MODEL_STRUCTURE]: '② MODEL: System Structure',
    [MeetingType.ANALYZE_FM]: '③ ANALYZE: Failure Modes Workshop',
    [MeetingType.ANALYZE_RC]: '③ ANALYZE: Root Causes Workshop',
    [MeetingType.MITIGATE_WORKSHOP]: '④ MITIGATE: Mitigations Workshop',
    [MeetingType.MITIGATE_APPROVAL]: '④ MITIGATE: Approval Review',
    [MeetingType.IMPLEMENT_REVIEW]: '⑤ IMPLEMENT: Implementation Review',
    [MeetingType.VERIFY_COMMISSION]: '⑥ VERIFY: Commissioning',
    [MeetingType.VERIFY_CLOSEOUT]: '⑥ VERIFY: Closeout',
    [MeetingType.GENERAL]: '📋 General Meeting',
    [MeetingType.AD_HOC]: '🔍 Ad-hoc Review',
};
/**
 * Phase number for each meeting type (1-6)
 */
export const MEETING_TYPE_PHASE = {
    [MeetingType.DEFINE_MISSION]: 1,
    [MeetingType.DEFINE_REQUIREMENTS]: 1,
    [MeetingType.MODEL_STRUCTURE]: 2,
    [MeetingType.ANALYZE_FM]: 3,
    [MeetingType.ANALYZE_RC]: 3,
    [MeetingType.MITIGATE_WORKSHOP]: 4,
    [MeetingType.MITIGATE_APPROVAL]: 4,
    [MeetingType.IMPLEMENT_REVIEW]: 5,
    [MeetingType.VERIFY_COMMISSION]: 6,
    [MeetingType.VERIFY_CLOSEOUT]: 6,
    [MeetingType.GENERAL]: null,
    [MeetingType.AD_HOC]: null,
};
/**
 * FEA-211: Work session status (simpler than meeting status)
 */
export var WorkSessionStatus;
(function (WorkSessionStatus) {
    WorkSessionStatus["ACTIVE"] = "ACTIVE";
    WorkSessionStatus["PAUSED"] = "PAUSED";
    WorkSessionStatus["COMPLETED"] = "COMPLETED";
    WorkSessionStatus["ABANDONED"] = "ABANDONED";
})(WorkSessionStatus || (WorkSessionStatus = {}));
/**
 * Type guard for WorkSessionStatus
 */
export function isWorkSessionStatus(value) {
    return Object.values(WorkSessionStatus).includes(value);
}
/**
 * FEA-211: Meeting session status
 */
export var MeetingSessionStatus;
(function (MeetingSessionStatus) {
    MeetingSessionStatus["ACTIVE"] = "ACTIVE";
    MeetingSessionStatus["PAUSED"] = "PAUSED";
    MeetingSessionStatus["COMPLETED"] = "COMPLETED";
})(MeetingSessionStatus || (MeetingSessionStatus = {}));
/**
 * Type guard for MeetingSessionStatus
 */
export function isMeetingSessionStatus(value) {
    return Object.values(MeetingSessionStatus).includes(value);
}
/**
 * FEA-211: Attendee role in a meeting
 */
export var AttendeeRole;
(function (AttendeeRole) {
    AttendeeRole["FACILITATOR"] = "FACILITATOR";
    AttendeeRole["NOTE_TAKER"] = "NOTE_TAKER";
    AttendeeRole["ATTENDEE"] = "ATTENDEE";
})(AttendeeRole || (AttendeeRole = {}));
/**
 * FEA-211: Agenda item status
 */
export var AgendaStatus;
(function (AgendaStatus) {
    AgendaStatus["PENDING"] = "PENDING";
    AgendaStatus["IN_PROGRESS"] = "IN_PROGRESS";
    AgendaStatus["COMPLETED"] = "COMPLETED";
    AgendaStatus["SKIPPED"] = "SKIPPED";
})(AgendaStatus || (AgendaStatus = {}));
/**
 * FEA-211: Action item priority
 */
export var ActionPriority;
(function (ActionPriority) {
    ActionPriority["LOW"] = "LOW";
    ActionPriority["MEDIUM"] = "MEDIUM";
    ActionPriority["HIGH"] = "HIGH";
    ActionPriority["CRITICAL"] = "CRITICAL";
})(ActionPriority || (ActionPriority = {}));
/**
 * FEA-211: Action item status
 */
export var ActionItemStatus;
(function (ActionItemStatus) {
    ActionItemStatus["OPEN"] = "OPEN";
    ActionItemStatus["IN_PROGRESS"] = "IN_PROGRESS";
    ActionItemStatus["COMPLETED"] = "COMPLETED";
    ActionItemStatus["CANCELLED"] = "CANCELLED";
})(ActionItemStatus || (ActionItemStatus = {}));
/**
 * FEA-211: Change type for session change tracking
 * Note: Aligns with existing Prisma ChangeType enum
 */
export var ChangeType;
(function (ChangeType) {
    ChangeType["CREATED"] = "CREATED";
    ChangeType["MODIFIED"] = "MODIFIED";
    ChangeType["DELETED"] = "DELETED";
    ChangeType["RESTORED"] = "RESTORED";
})(ChangeType || (ChangeType = {}));
/**
 * FEA-211: Entity types for session change tracking
 * Named SessionEntityType to avoid conflict with search.types.ts EntityType
 */
export var SessionEntityType;
(function (SessionEntityType) {
    SessionEntityType["FAILURE_MODE"] = "FAILURE_MODE";
    SessionEntityType["ROOT_CAUSE"] = "ROOT_CAUSE";
    SessionEntityType["MITIGATION"] = "MITIGATION";
    SessionEntityType["COMPONENT"] = "COMPONENT";
    SessionEntityType["FUNCTION"] = "FUNCTION";
    SessionEntityType["MECHANICAL_IMPL"] = "MECHANICAL_IMPL";
    SessionEntityType["ELECTRICAL_IMPL"] = "ELECTRICAL_IMPL";
    SessionEntityType["OPERATIONAL_IMPL"] = "OPERATIONAL_IMPL";
    SessionEntityType["CONTROL_LOOP"] = "CONTROL_LOOP";
    SessionEntityType["SOFTWARE_LOGIC"] = "SOFTWARE_LOGIC";
    SessionEntityType["DEVICE_CONFIG"] = "DEVICE_CONFIG";
    SessionEntityType["COMMISSIONING_ITEM"] = "COMMISSIONING_ITEM";
})(SessionEntityType || (SessionEntityType = {}));
/**
 * Type guard for SessionEntityType
 */
export function isSessionEntityType(value) {
    return Object.values(SessionEntityType).includes(value);
}
/**
 * Display labels for session entity types
 */
export const SESSION_ENTITY_TYPE_LABELS = {
    [SessionEntityType.FAILURE_MODE]: 'Failure Mode',
    [SessionEntityType.ROOT_CAUSE]: 'Root Cause',
    [SessionEntityType.MITIGATION]: 'Mitigation',
    [SessionEntityType.COMPONENT]: 'Component',
    [SessionEntityType.FUNCTION]: 'Function',
    [SessionEntityType.MECHANICAL_IMPL]: 'Mechanical Implementation',
    [SessionEntityType.ELECTRICAL_IMPL]: 'Electrical Implementation',
    [SessionEntityType.OPERATIONAL_IMPL]: 'Operational Implementation',
    [SessionEntityType.CONTROL_LOOP]: 'Control Loop',
    [SessionEntityType.SOFTWARE_LOGIC]: 'Software Logic',
    [SessionEntityType.DEVICE_CONFIG]: 'Device Configuration',
    [SessionEntityType.COMMISSIONING_ITEM]: 'Commissioning Item',
};
//# sourceMappingURL=enums.js.map